/**
 * Application Monitoring & Health Checks
 *
 * Provides utilities for monitoring application health, tracking
 * performance metrics, and logging structured events.
 */

import { captureMessage, captureError, Sentry } from "./sentry";

// Performance mark names
const PERF_MARKS = {
  APP_INIT_START: "app_init_start",
  APP_INIT_END: "app_init_end",
  PAGE_LOAD_START: "page_load_start",
  PAGE_LOAD_END: "page_load_end",
  API_CALL_START: "api_call_start",
  API_CALL_END: "api_call_end",
} as const;

// Health check status
export type HealthStatus = "healthy" | "degraded" | "unhealthy";

export interface HealthCheckResult {
  status: HealthStatus;
  timestamp: string;
  checks: {
    name: string;
    status: HealthStatus;
    latencyMs?: number;
    message?: string;
  }[];
}

/**
 * Marks the start of app initialization
 */
export function markAppInitStart() {
  if (typeof performance !== "undefined") {
    performance.mark(PERF_MARKS.APP_INIT_START);
  }
}

/**
 * Marks the end of app initialization and logs the duration
 */
export function markAppInitEnd() {
  if (typeof performance !== "undefined") {
    performance.mark(PERF_MARKS.APP_INIT_END);
    try {
      const measure = performance.measure(
        "app_initialization",
        PERF_MARKS.APP_INIT_START,
        PERF_MARKS.APP_INIT_END,
      );
      logMetric("app_init_duration_ms", measure.duration);
    } catch {
      // Marks may not exist, ignore
    }
  }
}

/**
 * Tracks a page view with timing information
 */
export function trackPageView(pathname: string) {
  if (typeof performance !== "undefined") {
    const navTiming = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    if (navTiming) {
      logMetric(
        "page_load_time_ms",
        navTiming.loadEventEnd - navTiming.startTime,
        {
          pathname,
        },
      );
    }
  }
}

/**
 * Tracks an API call with timing and success/failure
 */
export function trackApiCall(
  endpoint: string,
  durationMs: number,
  success: boolean,
  statusCode?: number,
) {
  logMetric("api_call_duration_ms", durationMs, {
    endpoint,
    success,
    statusCode,
  });

  if (!success && statusCode && statusCode >= 500) {
    captureMessage(`API error: ${endpoint} returned ${statusCode}`, "warning", {
      endpoint,
      statusCode,
      durationMs,
    });
  }
}

/**
 * Logs a metric with optional context and sends to Sentry
 */
export function logMetric(
  name: string,
  value: number,
  context?: Record<string, unknown>,
) {
  const metric = {
    name,
    value,
    timestamp: new Date().toISOString(),
    ...context,
  };

  // In development, log to console
  if (import.meta.env.DEV) {
    console.log("[Metric]", metric);
  }

  // Send metrics to Sentry
  try {
    // Use Sentry's setMeasurement for performance metrics
    const transaction = Sentry.getActiveSpan();
    if (transaction) {
      Sentry.setMeasurement(name, value, "millisecond");
    }

    // Also add as breadcrumb for context in error reports
    Sentry.addBreadcrumb({
      category: "metric",
      message: name,
      data: { value, ...context },
      level: "info",
    });
  } catch {
    // Ignore Sentry errors - metrics are non-critical
  }
}

/**
 * Tracks user actions for analytics
 */
export function trackUserAction(
  action: string,
  category: string,
  context?: Record<string, unknown>,
) {
  const event = {
    action,
    category,
    timestamp: new Date().toISOString(),
    ...context,
  };

  if (import.meta.env.DEV) {
    console.log("[UserAction]", event);
  }
}

/**
 * Send Web Vital metric to Sentry with proper tagging
 */
function sendWebVitalToSentry(
  name: "LCP" | "FID" | "CLS" | "INP" | "TTFB",
  value: number,
  rating: "good" | "needs-improvement" | "poor",
) {
  try {
    // Add as a custom measurement to the current transaction
    const transaction = Sentry.getActiveSpan();
    if (transaction) {
      const unit = name === "CLS" ? "" : "millisecond";
      Sentry.setMeasurement(name.toLowerCase(), value, unit);
    }

    // Send as a custom event for dedicated Web Vitals tracking
    Sentry.addBreadcrumb({
      category: "web-vital",
      message: `${name}: ${value.toFixed(2)} (${rating})`,
      data: { name, value, rating },
      level: rating === "poor" ? "warning" : "info",
    });

    // For poor vitals, record a metric instead of opening a noisy Sentry Issue.
    if (rating === "poor") {
      // Keep metric attributes low-cardinality (avoid including full paths with IDs).
      const route =
        typeof window !== "undefined"
          ? window.location.pathname === "/"
            ? "/"
            : `/${window.location.pathname.split("/")[1] || ""}`
          : undefined;

      const unit = name === "CLS" ? undefined : "millisecond";
      Sentry.metrics.distribution("web_vital.poor", value, {
        unit,
        attributes: {
          vital: name,
          ...(route ? { route } : {}),
        },
      });
    }
  } catch {
    // Ignore Sentry errors
  }
}

/**
 * Get Web Vital rating based on thresholds
 * Thresholds from https://web.dev/vitals/
 */
function getWebVitalRating(
  name: "LCP" | "FID" | "CLS" | "INP" | "TTFB",
  value: number,
): "good" | "needs-improvement" | "poor" {
  const thresholds: Record<string, [number, number]> = {
    LCP: [2500, 4000], // Good < 2.5s, Poor > 4s
    FID: [100, 300], // Good < 100ms, Poor > 300ms
    CLS: [0.1, 0.25], // Good < 0.1, Poor > 0.25
    INP: [200, 500], // Good < 200ms, Poor > 500ms
    TTFB: [800, 1800], // Good < 800ms, Poor > 1800ms
  };

  const [good, poor] = thresholds[name] || [0, 0];
  if (value <= good) return "good";
  if (value > poor) return "poor";
  return "needs-improvement";
}

/**
 * Performance observer for Core Web Vitals
 * Tracks LCP, FID, CLS and sends to Sentry
 */
export function initWebVitalsTracking() {
  if (typeof PerformanceObserver === "undefined") return;

  try {
    // Largest Contentful Paint
    const lcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      const lastEntry = entries[entries.length - 1];
      const value = lastEntry.startTime;
      logMetric("lcp_ms", value);
      sendWebVitalToSentry("LCP", value, getWebVitalRating("LCP", value));
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });

    // First Input Delay (approximated via first-input)
    const fidObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntries();
      entries.forEach((entry) => {
        const fidEntry = entry as PerformanceEventTiming;
        const value = fidEntry.processingStart - fidEntry.startTime;
        logMetric("fid_ms", value);
        sendWebVitalToSentry("FID", value, getWebVitalRating("FID", value));
      });
    });
    fidObserver.observe({ type: "first-input", buffered: true });

    // Cumulative Layout Shift
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((entryList) => {
      for (const entry of entryList.getEntries()) {
        const layoutShift = entry as PerformanceEntry & {
          hadRecentInput?: boolean;
          value?: number;
        };
        if (!layoutShift.hadRecentInput && layoutShift.value) {
          clsValue += layoutShift.value;
          logMetric("cls", clsValue);
          sendWebVitalToSentry(
            "CLS",
            clsValue,
            getWebVitalRating("CLS", clsValue),
          );
        }
      }
    });
    clsObserver.observe({ type: "layout-shift", buffered: true });

    // Time to First Byte (TTFB)
    try {
      const navEntry = performance.getEntriesByType(
        "navigation",
      )[0] as PerformanceNavigationTiming;
      if (navEntry) {
        const ttfb = navEntry.responseStart - navEntry.requestStart;
        logMetric("ttfb_ms", ttfb);
        sendWebVitalToSentry("TTFB", ttfb, getWebVitalRating("TTFB", ttfb));
      }
    } catch {
      // Navigation timing may not be available
    }
  } catch {
    // PerformanceObserver may not support all entry types
  }
}

/**
 * Health check for Supabase connection
 */
async function checkSupabaseHealth(): Promise<{
  status: HealthStatus;
  latencyMs: number;
  message?: string;
}> {
  const start = performance.now();
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  if (!supabaseUrl) {
    return {
      status: "unhealthy",
      latencyMs: 0,
      message: "VITE_SUPABASE_URL is not set",
    };
  }

  const healthUrl = `${supabaseUrl.replace(/\/+$/, "")}/functions/v1/health`;

  try {
    const response = await fetch(healthUrl, {
      method: "GET",
      cache: "no-store",
    });
    const latencyMs = performance.now() - start;

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      return {
        status: "unhealthy",
        latencyMs,
        message: `Health endpoint returned ${response.status}${errorText ? `: ${errorText.slice(0, 200)}` : ""}`,
      };
    }

    const data = (await response.json().catch(() => null)) as {
      status?: HealthStatus;
      checks?: unknown;
    } | null;
    const statusFromServer = data?.status;

    return {
      status: statusFromServer ?? (latencyMs > 2000 ? "degraded" : "healthy"),
      latencyMs,
    };
  } catch (error) {
    return {
      status: "unhealthy",
      latencyMs: performance.now() - start,
      message: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

/**
 * Run all health checks
 */
export async function runHealthChecks(): Promise<HealthCheckResult> {
  const checks = await Promise.all([
    checkSupabaseHealth().then((result) => ({
      name: "supabase",
      ...result,
    })),
  ]);

  // Determine overall status
  let overallStatus: HealthStatus = "healthy";
  if (checks.some((c) => c.status === "unhealthy")) {
    overallStatus = "unhealthy";
  } else if (checks.some((c) => c.status === "degraded")) {
    overallStatus = "degraded";
  }

  const result: HealthCheckResult = {
    status: overallStatus,
    timestamp: new Date().toISOString(),
    checks,
  };

  // Report unhealthy status to Sentry
  if (overallStatus === "unhealthy") {
    captureMessage("Health check failed", "error", { healthCheck: result });
  }

  return result;
}

/**
 * Error rate tracking for circuit breaker patterns
 */
class ErrorRateTracker {
  private errors: number[] = [];
  private readonly windowMs: number;
  private readonly threshold: number;

  constructor(windowMs = 60000, threshold = 0.5) {
    this.windowMs = windowMs;
    this.threshold = threshold;
  }

  recordSuccess() {
    this.cleanup();
    // Don't record successes, just cleanup old errors
  }

  recordError() {
    this.cleanup();
    this.errors.push(Date.now());
  }

  private cleanup() {
    const cutoff = Date.now() - this.windowMs;
    this.errors = this.errors.filter((t) => t > cutoff);
  }

  isCircuitOpen(minRequests = 10): boolean {
    this.cleanup();
    if (this.errors.length < minRequests) return false;
    return this.errors.length / minRequests > this.threshold;
  }

  getErrorRate(): number {
    this.cleanup();
    return this.errors.length;
  }
}

// Singleton error rate trackers for key services
export const apiErrorTracker = new ErrorRateTracker();
export const supabaseErrorTracker = new ErrorRateTracker();

/**
 * Wrap an async function with error tracking and circuit breaker
 */
export async function withErrorTracking<T>(
  fn: () => Promise<T>,
  tracker: ErrorRateTracker,
  serviceName: string,
): Promise<T> {
  if (tracker.isCircuitOpen()) {
    const error = new Error(`Circuit breaker open for ${serviceName}`);
    captureError(error, { serviceName, errorRate: tracker.getErrorRate() });
    throw error;
  }

  try {
    const result = await fn();
    tracker.recordSuccess();
    return result;
  } catch (error) {
    tracker.recordError();
    throw error;
  }
}
